/**
 * Global Ad Refresh Module
 *
 * Timer-based auto-refresh for eligible GPT display ad slots.
 * Reads config from window.whichcarAdRefresh (rendered inline by AdRefreshConfig::toArray()).
 *
 * Features:
 * - Slot discovery via googletag.cmd.push() synchronisation
 * - Size-based eligibility filtering
 * - Per-slot viewability-aware timers (≥50% visible)
 * - Staggered refresh queue (2s spacing)
 * - Tab visibility pause/resume
 * - bfcache reset on back/forward navigation
 * - Empty fill detection and timer pause
 * - Debug overlay via ?ad_refresh_debug=1
 * - dataLayer analytics events
 *
 * @see tasks/2-wip/0001-gpt-ad-refresh.md
 */

/** @type {Map<string, TrackedSlot>} */
const trackedSlots = new Map()

/** @type {string[]} FIFO queue of element IDs waiting to be refreshed */
const refreshQueue = []

/** @type {IntersectionObserver|null} */
let observer = null

/** @type {boolean} */
let debug = false

/** @type {number} Refresh interval in milliseconds */
let intervalMs = 30000

/** @type {Set<string>} Eligible size strings e.g. "970x250" */
let eligibleSizeSet = new Set()

/** @type {boolean} Whether discovery has already been wired up */
let initialised = false

/**
 * @typedef {Object} TrackedSlot
 * @property {googletag.Slot} slot - The GPT slot object
 * @property {string} elementId - DOM element ID
 * @property {number|null} timerId - Active setTimeout ID
 * @property {boolean} isViewable - Currently ≥50% visible
 * @property {number} elapsedMs - Time accumulated toward next refresh
 * @property {number} lastTickTime - Timestamp when timer last started/resumed
 * @property {number} refreshCount - Number of refreshes completed
 * @property {string} state - Current state for debug display
 * @property {boolean} emptyPaused - Paused due to empty fill
 * @property {string} renderedSize - Last rendered creative size e.g. "728x90"
 */

/**
 * Main entry point. Call once on window.load after setupTargeting().
 */
export function setupAdRefresh() {
  const config = window.whichcarAdRefresh
  if (!config || !config.enabled) return
  if (typeof googletag === "undefined") return

  // Prevent double-initialisation
  if (initialised) return
  initialised = true

  intervalMs = config.interval * 1000
  eligibleSizeSet = new Set(config.eligibleSizes)
  debug = new URLSearchParams(window.location.search).has("ad_refresh_debug")

  if (debug) log("Ad refresh initialising", { interval: config.interval, eligibleSizes: config.eligibleSizes })

  // Single IntersectionObserver for all tracked slots
  observer = new IntersectionObserver(handleIntersection, { threshold: [0, 0.5] })

  // Discover slots via cmd queue — runs after setupTargeting()'s cmd callback
  // which dispatches googletag:show and triggers Stimulus controllers' show()
  googletag.cmd.push(() => discoverSlots())

  // MutationObserver for lazy-loaded slots that appear after initial discovery
  const mutationObserver = new MutationObserver((mutations) => {
    let hasNewSlots = false
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType === Node.ELEMENT_NODE) {
          if (node.dataset?.controller?.includes("googletag") || node.querySelector?.("[data-controller*='googletag']")) {
            hasNewSlots = true
          }
        }
      }
    }
    if (hasNewSlots) {
      // Use googletag.cmd.push for synchronisation — ensures Stimulus controllers
      // have completed their own cmd.push callbacks before we discover
      googletag.cmd.push(() => discoverSlots())
    }
  })
  mutationObserver.observe(document.body, { childList: true, subtree: true })

  // Slots whose element exists at page load but whose GPT slot is defined later (the
  // gallery banner, defined on gallery open) produce no DOM mutation, so neither the
  // initial discovery nor the MutationObserver above ever sees them. They announce
  // themselves instead.
  document.addEventListener("googletag:slotdefined", () => {
    googletag.cmd.push(() => discoverSlots())
  })

  // The same slots are destroyed and redefined across the page's lifetime. Without an
  // untrack, trackedSlots keeps a destroyed slot reference and discoverSlots() skips
  // re-tracking on the next open, because the elementId is already present.
  document.addEventListener("googletag:slotdestroyed", (event) => {
    untrackSlot(event.detail?.elementId)
  })

  // Tab visibility: pause/resume all timers
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) pauseAllTimers()
    else resumeAllTimers()
  })

  // bfcache: reset all timers on back/forward navigation
  window.addEventListener("pageshow", (e) => {
    if (e.persisted) resetAllTimers()
  })

  // Single global slotRenderEnded listener for empty fill detection
  googletag.cmd.push(() => {
    googletag.pubads().addEventListener("slotRenderEnded", handleSlotRenderEnded)
  })

  // Start the stagger queue processor
  setInterval(processRefreshQueue, 2000)

  if (debug) renderDebugOverlays()
}

/**
 * Discover eligible slots from the global registry.
 * Idempotent — skips slots already tracked.
 */
function discoverSlots() {
  const registry = window.__whichcarAdSlots
  if (!registry) return

  for (const [elementId, slot] of Object.entries(registry)) {
    // Skip already-tracked slots
    if (trackedSlots.has(elementId)) continue

    if (!isSlotEligible(slot)) {
      if (debug) log(`Slot ${elementId}: ineligible — skipping`)
      continue
    }

    const tracked = {
      slot,
      elementId,
      sizes: getSlotSizeLabels(slot),
      timerId: null,
      isViewable: false,
      elapsedMs: 0,
      lastTickTime: 0,
      refreshCount: 0,
      state: "Waiting for view",
      emptyPaused: false,
      renderedSize: "",
    }

    trackedSlots.set(elementId, tracked)

    const element = document.getElementById(elementId)
    if (element) {
      observer.observe(element)
    }

    if (debug) log(`Slot ${elementId}: eligible — tracking`)
  }

  if (debug) updateDebugOverlays()
}

/**
 * Stop tracking a slot whose GPT slot has been destroyed.
 *
 * @param {string} [elementId]
 */
function untrackSlot(elementId) {
  if (!elementId) return

  const tracked = trackedSlots.get(elementId)
  if (!tracked) return

  pauseTimer(tracked)
  trackedSlots.delete(elementId)

  const element = document.getElementById(elementId)
  if (element && observer) {
    observer.unobserve(element)
    // Cleared so a redefined slot is re-evaluated from scratch rather than inheriting
    // the previous slot's bypass state.
    delete element.dataset.globalRefreshActive
  }

  if (debug) {
    log(`Slot ${elementId}: destroyed — untracked`)
    updateDebugOverlays()
  }
}

/**
 * Check if a slot has at least one eligible size.
 *
 * Uses slot.getSizes() which is an undocumented GPT runtime method (not in the
 * official TypeScript definitions) that returns base sizes as objects with
 * {width, height} properties. May also include "fluid" strings.
 * Verified working at runtime on whichcar.com.au — Feb 2026.
 *
 * @param {googletag.Slot} slot
 * @returns {boolean}
 */
function isSlotEligible(slot) {
  if (typeof slot.getSizes !== "function") return false
  const sizes = slot.getSizes()
  if (!sizes) return false

  for (const size of sizes) {
    // Skip string entries like "fluid"
    if (typeof size === "string") continue
    // getSizes() returns objects with width/height properties e.g. {width: 728, height: 90}
    const w = size.width ?? size[0]
    const h = size.height ?? size[1]
    if (w && h && eligibleSizeSet.has(`${w}x${h}`)) return true
  }

  return false
}

/**
 * Get human-readable size labels for a slot, grouped by eligibility.
 *
 * @param {googletag.Slot} slot
 * @returns {{ eligible: string[], ineligible: string[] }}
 */
function getSlotSizeLabels(slot) {
  if (typeof slot.getSizes !== "function") return { eligible: [], ineligible: [] }
  const sizes = slot.getSizes()
  if (!sizes) return { eligible: [], ineligible: [] }

  const eligible = []
  const ineligible = []

  for (const size of sizes) {
    if (typeof size === "string") {
      ineligible.push(size)
      continue
    }
    const w = size.width ?? size[0]
    const h = size.height ?? size[1]
    if (!w || !h) continue
    const label = `${w}x${h}`
    if (eligibleSizeSet.has(label)) {
      eligible.push(label)
    } else {
      ineligible.push(label)
    }
  }

  return { eligible, ineligible }
}

// ---------------------------------------------------------------------------
// Viewability (IntersectionObserver)
// ---------------------------------------------------------------------------

/**
 * Handle intersection changes for all tracked slots.
 *
 * @param {IntersectionObserverEntry[]} entries
 */
function handleIntersection(entries) {
  for (const entry of entries) {
    const elementId = entry.target.id
    const tracked = trackedSlots.get(elementId)
    if (!tracked) continue

    const wasViewable = tracked.isViewable
    tracked.isViewable = entry.intersectionRatio >= 0.5

    if (tracked.isViewable && !wasViewable) {
      // Entered view — start/resume timer (unless paused for empty fill)
      if (!tracked.emptyPaused) startTimer(tracked)
    } else if (!tracked.isViewable && wasViewable) {
      // Left view — pause timer
      pauseTimer(tracked)
    }
  }
}

// ---------------------------------------------------------------------------
// Per-Slot Timer Management
// ---------------------------------------------------------------------------

/**
 * Start or resume a slot's refresh timer.
 *
 * @param {TrackedSlot} tracked
 */
function startTimer(tracked) {
  if (tracked.timerId !== null) return // already running

  const remaining = intervalMs - tracked.elapsedMs
  tracked.lastTickTime = Date.now()
  tracked.state = `Next refresh in ${Math.ceil(remaining / 1000)}s`

  tracked.timerId = setTimeout(() => {
    tracked.timerId = null
    tracked.elapsedMs = 0
    tracked.state = "Queued for refresh"
    refreshQueue.push(tracked.elementId)
    if (debug) {
      log(`Slot ${tracked.elementId}: timer fired — queued for refresh`)
      updateDebugOverlays()
    }
  }, remaining)

  if (debug) {
    log(`Slot ${tracked.elementId}: timer started (${Math.ceil(remaining / 1000)}s remaining)`)
    updateDebugOverlays()
  }
}

/**
 * Pause a slot's timer, recording elapsed time.
 *
 * @param {TrackedSlot} tracked
 */
function pauseTimer(tracked) {
  if (tracked.timerId === null) return

  clearTimeout(tracked.timerId)
  tracked.timerId = null
  tracked.elapsedMs += Date.now() - tracked.lastTickTime
  tracked.state = "Paused \u2014 not in view"

  if (debug) {
    log(`Slot ${tracked.elementId}: timer paused (${Math.ceil(tracked.elapsedMs / 1000)}s elapsed)`)
    updateDebugOverlays()
  }
}

/**
 * Pause all tracked slot timers (tab hidden).
 */
function pauseAllTimers() {
  for (const tracked of trackedSlots.values()) {
    pauseTimer(tracked)
  }
  if (debug) log("All timers paused (tab hidden)")
}

/**
 * Resume timers for viewable slots (tab restored).
 */
function resumeAllTimers() {
  for (const tracked of trackedSlots.values()) {
    if (tracked.isViewable && !tracked.emptyPaused && tracked.timerId === null) {
      startTimer(tracked)
    }
  }
  if (debug) log("Timers resumed (tab visible)")
}

/**
 * Reset all timers to zero (bfcache restore).
 */
function resetAllTimers() {
  for (const tracked of trackedSlots.values()) {
    if (tracked.timerId !== null) {
      clearTimeout(tracked.timerId)
      tracked.timerId = null
    }
    tracked.elapsedMs = 0
    tracked.emptyPaused = false
    tracked.state = "Waiting for view"

    if (tracked.isViewable) {
      startTimer(tracked)
    }
  }
  if (debug) {
    log("All timers reset (bfcache restore)")
    updateDebugOverlays()
  }
}

// ---------------------------------------------------------------------------
// Staggered Refresh Queue
// ---------------------------------------------------------------------------

/**
 * Process the next slot in the refresh queue. Runs on a 2-second interval.
 */
function processRefreshQueue() {
  if (refreshQueue.length === 0) return

  const elementId = refreshQueue.shift()
  const tracked = trackedSlots.get(elementId)

  // Pre-refresh guards
  if (!tracked) return
  if (!window.__whichcarAdSlots?.[elementId]) return // slot destroyed
  if (!tracked.isViewable) {
    // No longer viewable — restart timer when it re-enters view
    tracked.state = "Waiting for view"
    if (debug) {
      log(`Slot ${elementId}: skipped refresh — no longer viewable`)
      updateDebugOverlays()
    }
    return
  }

  // Only refresh if the rendered creative is an eligible size
  if (tracked.renderedSize && !eligibleSizeSet.has(tracked.renderedSize)) {
    tracked.elapsedMs = 0
    tracked.state = `Skipped \u2014 ${tracked.renderedSize} not eligible`
    if (tracked.isViewable) startTimer(tracked)
    if (debug) {
      log(`Slot ${elementId}: skipped refresh \u2014 rendered size ${tracked.renderedSize} not eligible`)
      updateDebugOverlays()
    }
    return
  }

  // Execute refresh
  tracked.refreshCount++
  tracked.state = "Refreshing\u2026"

  googletag.cmd.push(() => {
    googletag.pubads().refresh([tracked.slot], { changeCorrelator: true })
  })

  // Mark slot as managed by global refresh (disables backfill retry in controller)
  const element = document.getElementById(elementId)
  if (element) {
    element.dataset.globalRefreshActive = "true"
  }

  // Analytics event
  const analyticsEvent = {
    event: "ad_refresh",
    ad_slot_id: elementId,
    ad_slot_size: tracked.renderedSize,
    ad_refresh_count: tracked.refreshCount,
    ad_refresh_interval: intervalMs / 1000,
  }
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push(analyticsEvent)

  // Reset timer — if still viewable, start counting again
  tracked.elapsedMs = 0
  if (tracked.isViewable && !tracked.emptyPaused) {
    startTimer(tracked)
  } else {
    tracked.state = "Waiting for view"
  }

  if (debug) {
    log(`Slot ${elementId}: refreshed (#${tracked.refreshCount}) — dataLayer event:`, analyticsEvent)
    updateDebugOverlays()
  }
}

// ---------------------------------------------------------------------------
// Empty Fill Handling
// ---------------------------------------------------------------------------

/**
 * Handle slotRenderEnded events for tracked slots.
 * Pauses timer on empty fill, resumes on subsequent non-empty fill.
 *
 * @param {googletag.events.SlotRenderEndedEvent} event
 */
function handleSlotRenderEnded(event) {
  const elementId = event.slot.getSlotElementId()
  const tracked = trackedSlots.get(elementId)
  if (!tracked) return

  // Capture the rendered creative size (event.size is [width, height])
  if (event.size && Array.isArray(event.size)) {
    tracked.renderedSize = `${event.size[0]}x${event.size[1]}`
  }

  if (event.isEmpty) {
    // Empty fill — pause this slot's timer
    tracked.emptyPaused = true
    pauseTimer(tracked)
    tracked.state = "Paused \u2014 empty ad fill"
    if (debug) {
      log(`Slot ${elementId}: empty fill — timer paused`)
      updateDebugOverlays()
    }
  } else if (tracked.emptyPaused) {
    // Non-empty fill after a previous empty — resume
    tracked.emptyPaused = false
    tracked.elapsedMs = 0
    if (tracked.isViewable) {
      startTimer(tracked)
    } else {
      tracked.state = "Waiting for view"
    }
    if (debug) {
      log(`Slot ${elementId}: creative received after empty — timer resumed`)
      updateDebugOverlays()
    }
  }
}

// ---------------------------------------------------------------------------
// Debug Overlay
// ---------------------------------------------------------------------------

/**
 * Log a debug message to the console.
 *
 * @param {string} message
 * @param {*} [data]
 */
function log(message, data) {
  if (data) {
    console.log(`[AdRefresh] ${message}`, data)
  } else {
    console.log(`[AdRefresh] ${message}`)
  }
}

/**
 * Create and render debug overlays for all tracked slots.
 */
function renderDebugOverlays() {
  // Re-render on a timer so state stays current
  setInterval(updateDebugOverlays, 1000)
}

/**
 * Update debug overlay content for all tracked slots.
 */
function updateDebugOverlays() {
  for (const [elementId, tracked] of trackedSlots) {
    const element = document.getElementById(elementId)
    if (!element) continue

    // Ensure parent is positioned for absolute overlay
    if (getComputedStyle(element).position === "static") {
      element.style.position = "relative"
    }

    let overlay = element.querySelector(".ad-refresh-debug")
    if (!overlay) {
      overlay = document.createElement("div")
      overlay.className = "ad-refresh-debug"
      Object.assign(overlay.style, {
        position: "absolute",
        top: "0",
        left: "0",
        zIndex: "99999",
        background: "rgba(0, 0, 0, 0.75)",
        color: "#0f0",
        fontFamily: "monospace",
        fontSize: "10px",
        lineHeight: "1.3",
        padding: "4px 6px",
        pointerEvents: "none",
        whiteSpace: "pre",
        maxWidth: "100%",
        textAlign: "left",
      })
      element.appendChild(overlay)
    }

    // Calculate live remaining time
    let remaining = ""
    if (tracked.timerId !== null) {
      const elapsed = tracked.elapsedMs + (Date.now() - tracked.lastTickTime)
      const remainingMs = Math.max(0, intervalMs - elapsed)
      remaining = ` ${Math.ceil(remainingMs / 1000)}s`
      tracked.state = `Next refresh in${remaining}`
    }

    const sizesLines = []
    if (tracked.sizes.eligible.length) sizesLines.push(`Refresh sizes: ${tracked.sizes.eligible.join(", ")}`)
    if (tracked.sizes.ineligible.length) sizesLines.push(`Other sizes: ${tracked.sizes.ineligible.join(", ")}`)

    const renderedLine = tracked.renderedSize ? `Rendered: ${tracked.renderedSize}\n` : ""

    overlay.textContent =
      `${elementId}\n` +
      sizesLines.join("\n") + "\n" +
      renderedLine +
      `${tracked.state}\n` +
      `Refreshed: ${tracked.refreshCount} times\n` +
      `In view: ${tracked.isViewable ? "Yes" : "No"}`
  }
}
